export enum TileType {
  Empty = 0,
  Brick = 1,
  Metal = 2,
  Water = 3,
  BaseCore = 4,
  BaseWall = 5,
  ReinforcedWall = 6,
}

export type Direction = 'up' | 'down' | 'left' | 'right';
export type Team = 'player' | 'enemy';
export type GameStatus = 'menu' | 'playing' | 'paused' | 'levelComplete' | 'gameOver';
export type EnemyKind = 'light' | 'assault' | 'heavy' | 'sniper' | 'boss';
export type BossVariant = 'siege' | 'hunter' | 'bulwark' | 'sniper';
export type EnvironmentKind = 'desert' | 'forest' | 'snow';
export type PlayerNumber = 1 | 2 | 3 | 4;
export type PlayerCount = PlayerNumber;
export type PowerupKind = 'rapid' | 'shield' | 'bomb' | 'life' | 'freeze' | 'repair' | 'baseShield' | 'jam' | 'upgrade' | 'heal';
export type UpgradeDropRate = 'off' | 'low' | 'normal' | 'high';
export type ObstacleDensity = 'low' | 'normal' | 'high' | 'dense';
export type EnemyAiMode = 'classic' | 'enhanced';
export type PlayerTankClass = 'standard' | 'assault' | 'heavy';
export type GameEventKind = 'message' | 'shoot' | 'hit' | 'explode' | 'powerup' | 'level' | 'countdown' | 'gameOver' | 'shake';
export type ExplosionEffectKind = 'flash' | 'shockwave' | 'burst';
export type HitVariant = 'terrain' | 'impact' | 'armor' | 'shield' | 'base';
export type ShootVariant = 'standard' | 'boss';

export interface Vec2 {
  x: number;
  y: number;
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface GameModeConfig {
  infiniteLives: boolean;
  invincibleBase: boolean;
  friendlyFire: boolean;
  upgradeDropRate: UpgradeDropRate;
  loseUpgradeOnDeath: boolean;
  playerCount: PlayerCount;
  levelTransitionDelay: number;
  obstacleDensity: ObstacleDensity;
  initialPlayerLevel: number;
  enemyAiMode: EnemyAiMode;
  playerTankClasses?: PlayerTankClass[];
}

export interface ControlState {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  fire: boolean;
}

export interface InputSnapshot {
  p1: ControlState;
  p2: ControlState;
  p3: ControlState;
  p4: ControlState;
  pausePressed: boolean;
}

export interface LevelDefinition {
  index: number;
  map: TileType[][];
  totalEnemies: number;
  maxEnemiesOnScreen: number;
  spawnInterval: number;
  spawnPoints: Vec2[];
  playerSpawns: Vec2[];
}

export interface EnemyArchetype {
  kind: EnemyKind;
  label: string;
  color: number;
  accent: number;
  maxSpeed: number;
  hp: number;
  fireRate: number;
  bulletSpeed: number;
  score: number;
  aggression: number;
  aimChance: number;
  size: number;
}

export interface TankEntity {
  id: string;
  team: Team;
  kind: 'player' | EnemyKind;
  playerNum?: PlayerNumber;
  color: number;
  accent: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  direction: Direction;
  alive: boolean;
  hp: number;
  maxHp: number;
  upgradeLevel: number;
  maxSpeed: number;
  acceleration: number;
  friction: number;
  fireRate: number;
  baseFireRate: number;
  bulletSpeed: number;
  fireCooldown: number;
  fireBuffer: number;
  rapidTimer: number;
  invincibleTimer: number;
  shieldTimer: number;
  freezeTimer: number;
  jamTimer: number;
  respawnTimer: number;
  aiTimer: number;
  aiDirection: Direction;
  stuckTimer: number;
  maneuverTimer: number;
  dodgeCooldownTimer: number;
  snapShotCooldownTimer: number;
  smokeTimer: number;
  shootWindupTimer: number;
  scoreValue: number;
  lastMoveBlocked: boolean;
  specialShotCounter: number;
  turnBias: -1 | 1;
  debugAiLabel?: string;
  bossVariant?: BossVariant;
  playerTankClass?: PlayerTankClass;
}

export interface UpcomingEnemyPreview {
  kind: EnemyKind;
  bossVariant?: BossVariant;
}

export interface BulletEntity {
  id: string;
  ownerTeam: Team;
  ownerPlayer?: PlayerNumber;
  x: number;
  y: number;
  vx: number;
  vy: number;
  direction: Direction;
  size: number;
  damage: number;
  armorPiercing?: boolean;
  pierceBricksRemaining?: number;
  pierceMetalRemaining?: number;
  explosive?: boolean;
  tracer?: boolean;
  suppressSeconds?: number;
  blastRadius?: number;
  blastDamage?: number;
  blastLightDamage?: number;
  blastTileDamage?: number;
  alive: boolean;
  ttl: number;
}

export interface PowerupEntity {
  id: string;
  kind: PowerupKind;
  x: number;
  y: number;
  size: number;
  life: number;
  bob: number;
}

export interface ParticleEntity {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: number;
  size: number;
  life: number;
  maxLife: number;
}

export interface BattleDecalEntity {
  id: string;
  kind: 'scorch' | 'crater';
  x: number;
  y: number;
  size: number;
  rotation: number;
  life: number;
  maxLife: number;
}

export interface ExplosionEffectEntity {
  id: string;
  kind: ExplosionEffectKind;
  x: number;
  y: number;
  color: number;
  size: number;
  maxSize: number;
  life: number;
  maxLife: number;
}

export interface GameEvent {
  kind: GameEventKind;
  text?: string;
  x?: number;
  y?: number;
  amount?: number;
  hitVariant?: HitVariant;
  shootVariant?: ShootVariant;
}

export interface GameStats {
  level: number;
  score: number;
  p1Lives: number;
  p2Lives: number;
  p1Hp: number;
  p1MaxHp: number;
  p1UpgradeLevel: number;
  p2Hp: number;
  p2MaxHp: number;
  p2UpgradeLevel: number;
  players: PlayerHudStats[];
  enemiesRemaining: number;
  baseState: string;
  status: GameStatus;
  continuesRemaining: number;
  playerCount: PlayerCount;
  upcomingWave: UpcomingEnemyPreview[];
}

export interface PlayerHudStats {
  playerNum: PlayerNumber;
  lives: number;
  hp: number;
  maxHp: number;
  upgradeLevel: number;
  active: boolean;
}
